import clsx from 'clsx'
import React, { FC, ReactNode } from 'react'
import styled from 'styled-components'

import { FormElementProps } from '@Components/FormElements/FormElementProps'
import { down } from '@Globals/Style/breakpoints'
import { errorMessage } from '@Globals/Style/mixins'

export interface BaseFormInputProps extends FormElementProps {
  children: ReactNode
  formActive: boolean
}

export const Adornment = styled.div`
  height: 44px;
  position: absolute;
  top: 0;
`

export const StartAdornment = styled(Adornment)`
  width: 22px;
  padding: 11px 0;
  left: 0;
`

export const EndAdornment = styled(Adornment)`
  width: 44px;
  right: 0;
`

const BaseFormInputWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['withoutLabelAnimation'].includes(prop),
})`
  display: block;
  margin-bottom: 10px;

  .formElementInner {
    height: 100%;
    position: relative;
  }

  label {
    position: absolute;
    margin-left: 12px;
    margin-top: 10px;
    display: block;
    color: ${({ theme }) => theme.colors.grey};
    font-size: 1.125rem;
    pointer-events: none;

    ${down('phablet')} {
      font-size: 1rem;
    }

    &.hidden {
      opacity: 0; // Don't set "display" or "visibility", as this will hide it from screen readers.
    }
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0 11px;
    font-size: 1.125rem;
    appearance: none;
    border-radius: ${({ theme: { borderRadius } }) => borderRadius};
  }

  select {
    cursor: pointer;
    padding-right: 30px;
  }

  textarea {
    padding-top: 9px;
    resize: vertical;
    min-height: 100px;
  }

  .errorMessage {
    ${errorMessage}
  }

  /* Variant */
  &.formElementVariantOutlined {
    input,
    select,
    textarea {
      border: 1px solid #ccc;
    }
  }

  &.formElementVariantUnderlined {
    input,
    select,
    textarea {
      border: none;
      border-bottom: 1px solid ${({ theme }) => theme.colors.grey};
      border-radius: 0;
    }
  }

  /* Sizes */
  &.formElementSizeMedium {
    input, select[size='1'], select[size=''] {
      height: 44px;
    }

    textarea {
      height: 150px;
    }
  }

  &.formElementSizeSmall {
    input, select[size='1'], select[size=''] {
      height: 30px;
    }

    input, select {
      font-size: 0.75rem;
    }

    .errorMessage {
      font-size: 0.75rem;
    }
  }

  /* Label animation */
  &.formElementWithLabelAnimation {
    ::placeholder {
      color: transparent;
    }

    label {
      transition: all 0.15s ease;
    }

    input {
      transition: padding 0.15s ease;
    }

    textarea {
      transition: padding 0.15s ease;
      padding-top: 7px;
    }

    .formActive {
      label {
        color: ${({ theme }) => theme.colors.greyLight};
        margin-top: 4px;
        font-size: 0.75rem;
      }

      input {
        padding-top: 16px;
      }

      select {
        // This is for the "multi-selector". The single-version comes further down.
        padding-top: 20px;
      }

      select[size='1'], select[size=''] {
        padding-top: 12px;
      }

      textarea {
        padding-top: 19px;
      }
    }
  }

  /* Adornments */
  &.formElementsWithStartAdornment {
    label {
      margin-left: 30px;
    }

    input {
      padding-left: 30px;
    }
  }
`

export const BaseFormInput: FC<BaseFormInputProps> = (
  {
    adornments,
    children,
    className,
    error,
    formActive,
    id,
    label,
    size = 'medium',
    variant = 'outlined',
    withoutLabelAnimation = false,
    wrapperClassName = '',
  }: BaseFormInputProps,
) => {
  const labelIsHidden = size === 'small' || withoutLabelAnimation

  return (
    <BaseFormInputWrapper
      className={clsx({
        [className || '']: className,
        formElementSizeMedium: size === 'medium',
        formElementSizeSmall: size === 'small',
        formElementVariantOutlined: variant === 'outlined',
        formElementVariantUnderlined: variant === 'underlined',
        formElementWithLabelAnimation: size !== 'small' && !withoutLabelAnimation,
        formElementsWithEndAdornment: adornments?.end,
        formElementsWithStartAdornment: adornments?.start,
        [wrapperClassName]: wrapperClassName,
      })}
    >
      <div
        className={clsx({
          formActive,
          formElementInner: true,
        })}
      >
        <label
          className={clsx({ hidden: labelIsHidden })}
          htmlFor={id}
        >
          {label}
        </label>
        {adornments?.start && <StartAdornment>{adornments.start}</StartAdornment>}
        {adornments?.end && <EndAdornment>{adornments.end}</EndAdornment>}
        {children}
      </div>
      {error && <span className="errorMessage">{error}</span>}
    </BaseFormInputWrapper>
  )
}
